import { find, map, reduce, some } from "lodash"
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range"

const splitTimeString = timeString => {
  const [hour, minute, second] = timeString.split(":").map(Number)
  return { hour, minute, second }
}

const getShifts = (currentDate, validFrom, validTo) => {
  const startOfShift = currentDate.clone().set(splitTimeString(validFrom))
  const endOfShift = currentDate.clone().set(splitTimeString(validTo))

  const isEndAtMidnight = endOfShift.hour() === 0
  const isNightShift = endOfShift.diff(startOfShift, "hours") < 0 && !isEndAtMidnight
  if (isNightShift || isEndAtMidnight) endOfShift.add(1, "day")
  return {
    startOfShift,
    endOfShift,
    isNightShift,
  }
}

export class CapacityStream {
  constructor(capacityPlans, from, timezone = "UTC") {
    this.capacityPlans = capacityPlans
    this.timezone = timezone
    this.from = from
  }

  alignToLocalMidnight = (dateString, endOfDay = false) => {
    const format = moment.isMoment(dateString) ? "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ" : "YYYY-MM-DD"
    const date = moment.tz(dateString, format, this.timezone).startOf("day")
    return endOfDay ? date.add(1, "day") : date
  }

  read(until = this.alignToLocalMidnight(this.from).add(7, "d")) {
    const startDate = this.alignToLocalMidnight(this.from).subtract(1, "days")
    const endDate = this.alignToLocalMidnight(until, true)

    const sortedCapacityPlans = map(this.capacityPlans, ({ validFrom, validTo, availabilities }) => ({
      validFrom: this.alignToLocalMidnight(validFrom),
      validTo: validTo ? this.alignToLocalMidnight(validTo, true) : endDate,
      availabilities,
    })).sort((a, b) => b.validFrom.diff(a.validFrom))

    const gaps = []
    let previousShiftNightShiftStart = null
    while (startDate.isBefore(endDate)) {
      const capacityPlanForTheDay = find(sortedCapacityPlans, capacityPlan =>
        startDate.isBetween(capacityPlan.validFrom, capacityPlan.validTo, undefined, "[)")
      )

      if (!capacityPlanForTheDay) {
        startDate.startOf("day").add(1, "day")
        previousShiftNightShiftStart = null
        continue
      }

      if (capacityPlanForTheDay.validFrom.isAfter(startDate)) {
        startDate.set(capacityPlanForTheDay.validFrom.toObject())
      }

      const availabilityOfTheDay = find(
        capacityPlanForTheDay.availabilities,
        availability => startDate.day() === availability.weekday && availability.isActive
      )

      const endOfDay = startDate.clone().startOf("day").add(1, "day")

      // handle sunday edge case
      if (startDate.day() === 0) {
        const capacityPlanForTheNextDay = find(sortedCapacityPlans, capacityPlan =>
          endOfDay.isBetween(capacityPlan.validFrom, capacityPlan.validTo, undefined, "[)")
        )
        if (capacityPlanForTheNextDay) {
          const availabilityOfNextSunday = find(
            capacityPlanForTheNextDay.availabilities,
            availability => availability.weekday === 0 && availability.isActive
          )
          if (!availabilityOfTheDay && !availabilityOfNextSunday) {
            gaps.push({
              start: startDate.toDate(),
              end: endOfDay.toDate(),
            })
            startDate.set(endOfDay.toObject())
            continue
          }
          if (availabilityOfNextSunday) {
            const {
              startOfShift: startOfNextSundayShift,
              endOfShift: endOfNextSundayShift,
              isNightShift: isNextSundayNightShift,
            } = getShifts(startDate, availabilityOfNextSunday.timeFrom, availabilityOfNextSunday.timeTo)
            if (isNextSundayNightShift) {
              if (availabilityOfTheDay) {
                const { startOfShift, endOfShift } = getShifts(
                  startDate,
                  availabilityOfTheDay.timeFrom,
                  availabilityOfTheDay.timeTo
                )
                gaps.push({
                  start: startDate.toDate(),
                  end: startOfShift.toDate(),
                })

                gaps.push({
                  start: endOfShift.toDate(),
                  end: startOfNextSundayShift.toDate(),
                })
                startDate.set(endOfNextSundayShift.toObject())
              } else {
                gaps.push({
                  start: startDate.toDate(),
                  end: startOfNextSundayShift.toDate(),
                })
                startDate.set(endOfNextSundayShift.toObject())
              }
              continue
            } else if (!availabilityOfTheDay) {
              gaps.push({
                start: startDate.toDate(),
                end: endOfDay.toDate(),
              })
              startDate.set(endOfDay.toObject())
              continue
            }
          } else if (!availabilityOfTheDay) {
            gaps.push({
              start: startDate.toDate(),
              end: endOfDay.toDate(),
            })
            startDate.set(endOfDay.toObject())
            continue
          }
        }
      }

      if (!availabilityOfTheDay && !startDate.day() !== 0) {
        gaps.push({
          start: startDate.toDate(),
          end: endOfDay.toDate(),
        })
        startDate.set(endOfDay.toObject())
        continue
      }

      const { startOfShift, endOfShift, isNightShift } = getShifts(
        startDate,
        availabilityOfTheDay.timeFrom,
        availabilityOfTheDay.timeTo
      )

      if (startDate.day() === 0 && isNightShift) {
        gaps.push({
          start: startDate.toDate(),
          end: endOfDay.toDate(),
        })
        startDate.set(endOfDay.toObject())
        continue
      }

      if (startOfShift.isSame(endOfShift)) {
        if (previousShiftNightShiftStart) {
          gaps.push({ start: previousShiftNightShiftStart.toDate(), end: startOfShift.toDate() })
          previousShiftNightShiftStart = null
        } else {
          gaps.push({ start: startDate.toDate(), end: startOfShift.toDate() })
        }

        startDate.set(endOfShift.toObject()).add(1, "day")
        continue
      }
      if (isNightShift) {
        previousShiftNightShiftStart = startOfShift
        const nextDayHasCapacityPlan = some(sortedCapacityPlans, capacityPlan =>
          endOfDay.isBetween(capacityPlan.validFrom, capacityPlan.validTo, undefined, "[)")
        )
        if (endOfShift.isSameOrAfter(capacityPlanForTheDay.validTo) && !nextDayHasCapacityPlan) {
          gaps.push({ start: startDate.toDate(), end: endOfDay.toDate() })
        } else {
          gaps.push({ start: startDate.toDate(), end: startOfShift.toDate() })
        }

        startDate.set(endOfShift.toObject())
        continue
      } else {
        gaps.push({
          start: startDate.toDate(),
          end: startOfShift.toDate(),
        })
        gaps.push({
          start: endOfShift.toDate(),
          end: endOfDay.toDate(),
        })

        startDate.set(endOfDay.toObject())
        previousShiftNightShiftStart = null
      }
      if (previousShiftNightShiftStart) {
        gaps.push({
          start: previousShiftNightShiftStart.toDate(),
          end: startOfShift.isBefore(startDate) ? startOfShift.toDate() : startDate.toDate(),
        })
        previousShiftNightShiftStart = null
      }
    }

    const mergedGaps = reduce(
      gaps,
      (result, currentGap) => {
        if (moment(currentGap.start).isSameOrAfter(moment(currentGap.end))) return result

        if (result.length === 0) return [currentGap]
        const previousGap = result.pop()
        if (moment(currentGap.start).isSame(moment(previousGap.end))) {
          result.push({
            start: previousGap.start,
            end: currentGap.end,
          })
        } else {
          result.push(previousGap, currentGap)
        }

        return result
      },
      []
    )

    return mergedGaps
  }

  reset(from) {
    this.from = from
    return this
  }
}
